import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { SubpageLayout } from '../components/SubpageLayout';
import { allProjects, getProject } from '../utils/projects';

/** Project case-study page, generated from the project data. */
export function Project() {
  const {
    slug
  } = useParams();
  const project = getProject(slug);
  if (!project) return <Navigate to="/work" replace data-id="element-572" data-mp="vrwz0o86nkt6" />;
  const index = allProjects.indexOf(project);
  const next = allProjects[(index + 1) % allProjects.length];
  const facts = [{
    label: 'Client',
    value: project.client
  }, {
    label: 'Location',
    value: project.location
  }, {
    label: 'Year',
    value: project.year
  }, {
    label: 'Services',
    value: project.services.join(', ')
  }].filter(f => f.value);
  const chapters = [{
    label: 'The brief',
    text: project.challenge
  }, {
    label: 'Approach',
    text: project.approach
  }, {
    label: 'Outcome',
    text: project.outcome
  }].filter(c => c.text);
  return <SubpageLayout title={project.title} data-id="element-573" data-mp="26y8rxlgee6zb">
      <article className="px-page pb-20 pt-36 md:pb-28 md:pt-44" data-id="element-574" data-mp="22w5560oa3fyq">
        <Link to="/work" className="group inline-flex items-center gap-2 text-[13px] text-fg/50 transition-colors duration-200 hover:text-fg" data-id="element-575" data-mp="22xy9vafbzq43">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-576" data-mp="2bvs8wt4hcxuy" />
          All work
        </Link>

        {project.tags.length > 0 && <ul className="mt-10 flex flex-wrap items-center gap-x-2 text-[12px] uppercase tracking-[0.04em] text-fg/45" data-id="element-577" data-mp="2zmxiwrnqovxc">
            {project.tags.map((tag, i) => <li key={tag} className="flex items-center gap-x-2" data-id="element-578" data-mp="2upnn8u9rbo12" data-mpi={i}>
                {i > 0 && <span className="h-2.5 w-px bg-fg/25" aria-hidden="true" data-id="element-579" data-mp="39ktutloi3uwi" />}
                {tag}
              </li>)}
          </ul>}

        <h1 className="mt-4 font-serif text-[44px] font-normal leading-[1.02] tracking-[-0.01em] md:text-[72px]" data-id="element-580" data-mp="3bp78b6efvsgb">
          {project.title}
        </h1>
        <p className="mt-5 max-w-[640px] text-[18px] leading-[1.5] text-fg/60" data-id="element-581" data-mp="3glpb40q7lgq0">{project.summary}</p>

        {/* Facts */}
        <dl className="mt-12 grid grid-cols-2 gap-x-[var(--gutter)] gap-y-6 border-t border-line pt-5 md:grid-cols-4" data-id="element-582" data-mp="3qdlbjifrpixe">
          {facts.map(f => <div key={f.label} data-id="element-583" data-mp="3vbwnbypmi3i9">
              <dt className="text-[13px] text-fg/45" data-id="element-584" data-mp="3vf0vf767lje7">{f.label}</dt>
              <dd className="mt-1 text-[15px] leading-[1.45] text-fg" data-id="element-585" data-mp="37at7j4w3fix">{f.value}</dd>
            </div>)}
        </dl>

        {/* Hero image */}
        <div className="mt-12 aspect-[16/9] overflow-hidden bg-fg/5 md:mt-16" data-id="element-586" data-mp="d6t9npllcj68">
          <img src={project.image} alt={`${project.title} project`} className="h-full w-full object-cover" data-id="element-587" data-mp="d4p8hff2ty9n" />
        </div>

        {/* Chapters */}
        <div className="mt-16 md:mt-24" data-id="element-588" data-mp="2ezhge8xg1oqr">
          {chapters.map(c => <section key={c.label} className="grid gap-3 border-t border-line py-8 md:grid-cols-12 md:gap-x-6 md:py-10" data-id="element-589" data-mp="27ffas8nluixt">
              <h2 className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60 md:col-span-3" data-id="element-590" data-mp="22n5n6z9ypuyp">{c.label}</h2>
              <p className="max-w-[680px] text-[18px] leading-[1.6] text-fg/75 md:col-span-7 md:col-start-5 md:text-[20px]" data-id="element-591" data-mp="1xplw2ys61npc">
                {c.text}
              </p>
            </section>)}
        </div>

        {/* Results */}
        {project.stats.length > 0 && <dl className="grid gap-8 border-t border-line pt-10 md:grid-cols-3 md:gap-x-6" data-id="element-592" data-mp="2lfpyx3xj5jfa">
            {project.stats.map(s => <div key={s.label} className="flex flex-col-reverse gap-2" data-id="element-593" data-mp="2ghok4v75069m">
                <dt className="text-[13px] text-fg/50" data-id="element-594" data-mp="2bfphjnj81ddq">{s.label}</dt>
                <dd className="font-serif text-[40px] font-normal leading-none tracking-[-0.01em] text-fg md:text-[48px]" data-id="element-595" data-mp="150yat37f2ybs">
                  {s.value}
                </dd>
              </div>)}
          </dl>}
      </article>

      {/* Next project */}
      <section aria-label="Next project" className="border-t border-line px-page pb-20 pt-12 md:pb-28" data-id="element-596" data-mp="2tm9bnqmwtc8m">
        <Link to={`/work/${next.slug}`} className="group grid gap-6 focus:outline-none md:grid-cols-12 md:gap-x-6" data-id="element-597" data-mp="2exd9ga7jatfx">
          <div className="md:col-span-5" data-id="element-598" data-mp="2jvwveam4o58a">
            <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60" data-id="element-599" data-mp="2jwy8slqm93t6">Next project</p>
            <div className="mt-4 flex items-start justify-between gap-6" data-id="element-600" data-mp="37msfelkcdw4z">
              <h2 className="font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] text-fg/85 transition-colors duration-200 group-hover:text-fg md:text-[48px]" data-id="element-601" data-mp="3cjsoz6gehh0u">
                {next.title}
              </h2>
              <ArrowUpRightIcon className="mt-3 h-5 w-5 shrink-0 text-fg/50 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden="true" data-id="element-602" data-mp="32l37hesf1yl4" />
            </div>
            <p className="mt-3 max-w-[420px] text-[15px] leading-[1.5] text-fg/50" data-id="element-603" data-mp="9bz06qscn8ai">{next.summary}</p>
          </div>
          <div className="aspect-[3/2] overflow-hidden bg-fg/5 group-focus-visible:ring-2 group-focus-visible:ring-fg md:col-span-7" data-id="element-604" data-mp="iakdo7iyrx4f">
            <img src={next.image} alt="" loading="lazy" className="h-full w-full object-cover opacity-[0.88] transition-opacity duration-200 group-hover:opacity-100" data-id="element-605" data-mp="eaa9n0eqfgs7" />
          </div>
        </Link>
      </section>
    </SubpageLayout>;
}